/*
 * Ryan O'Dowd
 * 2023-08-02
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
// @TODO: should this be used in Settings as well?
import React, {
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import Searchbar from '@components/common/Searchbar';
import getStyles from './styles';
import hooks from '@hooks';
import {
  updateUserSettings,
} from '@utils/store/actions';

const TranslationSelectionDialog = (props: TranslationSelectionDialogProps) => {
  const dispatch = hooks.useTypedDispatch();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const isAdmin = hooks.useIsAdmin();

  const [_searchText, setSearchText] = useState('');

  const locales = hooks.useTypedSelector((state) => state.locales);
  const userSettings = hooks.useTypedSelector((state) => state.userSettings);
  const translationsByLocale = hooks.useTypedSelector((state) => {
    const selectableTranslations = state.translations.filter((t) => {
      return (t.disabled === 0 && t.downloadable === 1) || isAdmin;
    });

    const byLocale = {};
    selectableTranslations.forEach((t) => {
      if (!byLocale[t.locale_id]) {
        byLocale[t.locale_id] = [];
      }

      byLocale[t.locale_id].push(t);
    });

    Object.keys(byLocale).forEach((key) => {
      byLocale[key] = byLocale[key].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    });

    return byLocale;
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const localeOrder = Object.keys(translationsByLocale).sort((a, b) => {
    // ensure user's locale shows up first
    if (a === userSettings.locale_id && b !== userSettings.locale_id) return -1;
    if (b === userSettings.locale_id && a !== userSettings.locale_id) return 1;

    return +a - +b;
  });
  const sortedAndFilteredTranslations = [];
  localeOrder.forEach((localeId) => {
    const thisLocale = locales[localeId];
    const filtered = [];
    translationsByLocale[localeId].forEach((t) => {
      if (!_searchText || [t.name, t.abbreviation, t.name_english, thisLocale.iso_639_3, thisLocale.datefns_code, thisLocale.language_name, thisLocale.language_name_local].some((val) => val.toLocaleLowerCase().includes(_searchText.toLocaleLowerCase()))) {
        filtered.push(t);
      }
    });
    sortedAndFilteredTranslations.push(...filtered);
  });

  const preferredTranslation = hooks.usePreferredTranslation();

  if (!preferredTranslation) { // @TODO: shouldn't need this, but getting errors on localhost without it...
    return null;
  }

  return (
    <Dialog
      visible={props.isOpen}
      onDismiss={() => props.onClose(preferredTranslation.id)}
    >
      <Dialog.Title>{props.title || i18n('translation')}</Dialog.Title>
      <Dialog.Content disableScroll={true}>
        {props.description}
        <Searchbar
          whiteVariant={true}
          onChangeText={(searchText) => setSearchText(searchText)}
          value={_searchText}
          autoCorrect={false}// there are too many words that make auto-correct difficult and confusing
          placeholder={i18n('searchTranslations')}
          autoFocus={false}
        />
        <ScrollView showsVerticalScrollIndicator={false}>
          {sortedAndFilteredTranslations.length === 0 &&
            <EmptyScreen.Wrapper avoidKeyboard={true}>
              <EmptyScreen.Text extraStyle={styles.emptyText}>
                {i18n('noMatches', _searchText.trim())}
              </EmptyScreen.Text>
            </EmptyScreen.Wrapper>
          }
          <View>
            <ButtonGroup
              options={sortedAndFilteredTranslations.map((t, index) => {
                const option: any = {
                  title: `${t.name} (${t.abbreviation})`,
                  value: t.id,
                };

                if (index === 0 || (t.locale_id !== sortedAndFilteredTranslations[index - 1].locale_id)) {
                  option.sectionTitle = locales[t.locale_id]?.language_name_local;
                }

                return option;
              })}
              selectedValue={preferredTranslation.id}
              onSelect={(newPreferredTranslationId) => {
                dispatch(updateUserSettings({preferred_translation_id: newPreferredTranslationId}));
                if (props.autoClose) {
                  props.onClose(preferredTranslation.id);
                }
              }}
              isVertical={true}
            />
          </View>
        </ScrollView>
      </Dialog.Content>
      {!props.autoClose &&
        <Dialog.Actions>
          <Button
            mode='contained'
            disabled={!preferredTranslation.id || props.continueButtonDisabledWithActivityIndicator}
            onPress={() => props.onClose(preferredTranslation.id)}
            loading={props.continueButtonDisabledWithActivityIndicator}
          >
            {props.continueButtonDisabledWithActivityIndicator
              ? 'Loading'
              : i18n('continue')/* @TODO: this is only used for onboarding right now, so it's okay that it's "continue", but this really should be more generic like "okay" or "done" or passed as a prop */
            }
          </Button>
        </Dialog.Actions>
      }
      {/* @TODO: show close/cancel button if user doesn't want to pick a new one? */}
    </Dialog>
  );
};

class TranslationSelectionDialogProps {
  isOpen: boolean;
  autoClose: boolean;
  onClose: (translationId: number) => void;

  continueButtonDisabledWithActivityIndicator?: boolean;
  title?: string;
  description?: any;
};

export default TranslationSelectionDialog;
